import React from "react";
import styled, { keyframes } from "styled-components";
import ModalWrapper from "./ModalWrapper";

// Fade-in animation
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Backdrop for modal
const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

// Modal container
const ModalContainer = styled.div`
  width: 90%;
  max-width: 580px;
  background: #fff;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 80vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 1025px) {
    padding: 2rem;
  }
`;

// Modal component
const CustomModal = ({
  open,
  onClose,
  showCloseIcon = true,
  setOpen,
  children,
  closeOnOverlayClick = true,
}) => {
  if (!open) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };
  if (!open) return null;

  return (
    <Backdrop onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalWrapper onClose={onClose}>{children}</ModalWrapper>
      </ModalContainer>
    </Backdrop>
  );
};

/**
 * A small modal component that wraps the CustomModal component.
 *
 * @param {bool} open - Whether the modal is open or not.
 * @param {func} setOpen - A function to update the open state.
 * @param {ReactElement} children - The content of the modal.
 * @param {bool} closeOnOverlayClick - Whether to close the modal when the overlay is clicked. Default is true.
 * @param {bool} showCloseIcon - Whether to show the close icon. Default is true.
 * @param {func} closeModal - A function to call when the modal is closed.
 * @returns {ReactElement} A JSX element representing the small modal.
 */
const SmallModal = ({
  open,
  setOpen,
  children,
  closeOnOverlayClick = true,
  showCloseIcon = true,
  closeModal,
}) => {
  const handleCloseModal = () => {
    if (setOpen) {
      setOpen(false);
    } else if (closeModal) {
      closeModal();
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={handleCloseModal}
      showCloseIcon={showCloseIcon}
      closeOnOverlayClick={closeOnOverlayClick}
    >
      {children}
    </CustomModal>
  );
};

/**
 * A medium-sized modal component that wraps the CustomModal component.
 *
 * @param {bool} open - Whether the modal is open or not.
 * @param {func} setOpen - A function to update the open state.
 * @param {ReactElement} children - The content of the modal.
 * @param {bool} closeOnOverlayClick - Whether to close the modal when the overlay is clicked. Default is true.
 * @param {bool} showCloseIcon - Whether to show the close icon. Default is true.
 * @param {func} closeModal - A function to call when the modal is closed.
 * @returns {ReactElement} A JSX element representing the medium modal.
 */

const MediumModal = ({
  open,
  setOpen,
  children,
  closeOnOverlayClick = true,
  showCloseIcon = true,
  closeModal,
}) => {
  const handleCloseModal = () => {
    if (setOpen) {
      setOpen(false);
    } else if (closeModal) {
      closeModal();
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={handleCloseModal}
      showCloseIcon={showCloseIcon}
      closeOnOverlayClick={closeOnOverlayClick}
      maxHeight="734px"
    >
      {children}
    </CustomModal>
  );
};

/**
 * A large modal component that wraps the CustomModal component.
 *
 * @param {bool} open - Whether the modal is open or not.
 * @param {func} setOpen - A function to update the open state.
 * @param {ReactElement} children - The content of the modal.
 * @param {bool} closeOnOverlayClick - Whether to close the modal when the overlay is clicked. Default is true.
 * @param {bool} showCloseIcon - Whether to show the close icon. Default is true.
 * @param {func} closeModal - A function to call when the modal is closed.
 * @returns {ReactElement} A JSX element representing the large modal.
 */
const LargeModal = ({
  open,
  setOpen,
  children,
  closeOnOverlayClick = true,
  showCloseIcon = true,
  closeModal,
}) => {
  const handleCloseModal = () => {
    if (setOpen) {
      setOpen(false);
    } else if (closeModal) {
      closeModal();
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={handleCloseModal}
      showCloseIcon={showCloseIcon}
      closeOnOverlayClick={closeOnOverlayClick}
      maxHeight="80vh"
    >
      {children}
    </CustomModal>
  );
};
const ToastModal = ({
  open,
  setOpen,
  children,
  closeOnOverlayClick = true,
  showCloseIcon = true,
  closeModal,
}) => {
  /**
   * Closes the modal by setting open to false if setOpen is defined,
   * otherwise calls the closeModal function if it is defined.
   */
  const handleCloseModal = () => {
    if (setOpen) {
      setOpen(false);
    } else if (closeModal) {
      closeModal();
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={handleCloseModal}
      showCloseIcon={showCloseIcon}
      closeOnOverlayClick={closeOnOverlayClick}
      maxHeight="auto"
      maxWidth="90%"
      padding="0"
    >
      {children}
    </CustomModal>
  );
};
export { SmallModal, MediumModal, LargeModal, ToastModal };
